import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../App';
import { Button } from '../ui/button';
import { 
  Package, ShoppingCart, FileText, Truck, Landmark, Calculator,
  User, LogOut, MessageSquare, Heart, ClipboardList, Building2,
  Globe, Users, Shield, BarChart3, Settings, Home, ChevronLeft, ChevronRight,
  Menu, Award
} from 'lucide-react';

// Sidebar links configuration per role - using translation keys
const getSidebarLinks = (role, t) => {
  const links = {
    sme: [
      { icon: Home, label: t('sidebar.dashboard'), href: '/dashboard' },
      { icon: Package, label: t('sidebar.myProducts'), href: '/my-products' },
      { icon: ClipboardList, label: t('sidebar.orders'), href: '/orders' },
      { icon: MessageSquare, label: t('sidebar.inquiries'), href: '/inquiries' },
      { icon: ShoppingCart, label: t('sidebar.marketplace'), href: '/marketplace' },
      { icon: FileText, label: t('sidebar.documents'), href: '/documents' },
      { icon: Award, label: t('sidebar.certificateOfOrigin'), href: '/coo' },
      { icon: Truck, label: t('sidebar.logistics'), href: '/logistics' },
      { icon: Landmark, label: t('sidebar.tradeFinance'), href: '/trade-finance' },
      { icon: Shield, label: t('sidebar.tradeCompliance'), href: '/compliance-engine' },
      { icon: BarChart3, label: t('sidebar.myReports'), href: '/my-reports' },
    ],
    buyer: [
      { icon: Home, label: t('sidebar.dashboard'), href: '/buyer/dashboard' },
      { icon: ShoppingCart, label: t('sidebar.marketplace'), href: '/marketplace' },
      { icon: MessageSquare, label: t('sidebar.myInquiries'), href: '/buyer/inquiries' },
      { icon: ClipboardList, label: t('sidebar.myOrders'), href: '/buyer/orders' },
      { icon: Heart, label: t('sidebar.savedSuppliers'), href: '/buyer/saved-suppliers' },
      { icon: FileText, label: t('sidebar.documents'), href: '/documents' },
      { icon: Shield, label: t('sidebar.tradeCompliance'), href: '/compliance-engine' },
      { icon: BarChart3, label: t('sidebar.myReports'), href: '/my-reports' },
    ],
    logistics: [
      { icon: Home, label: t('sidebar.dashboard'), href: '/logistics/dashboard' },
      { icon: Truck, label: t('sidebar.shipments'), href: '/logistics/shipments' },
      { icon: ClipboardList, label: t('sidebar.quoteRequests'), href: '/logistics/quotes' },
      { icon: BarChart3, label: t('sidebar.analytics'), href: '/logistics/analytics' },
      { icon: Settings, label: t('sidebar.settings'), href: '/logistics/settings' },
    ],
    finance: [
      { icon: Home, label: t('sidebar.dashboard'), href: '/finance/dashboard' },
      { icon: FileText, label: t('sidebar.applications'), href: '/finance/applications' },
      { icon: ClipboardList, label: t('sidebar.activeLoans'), href: '/finance/loans' },
      { icon: BarChart3, label: t('sidebar.analytics'), href: '/finance/analytics' },
      { icon: Settings, label: t('sidebar.settings'), href: '/finance/settings' },
    ],
    government: [
      { icon: Home, label: t('sidebar.dashboard'), href: '/government/dashboard' },
      { icon: Building2, label: t('sidebar.verifySmes'), href: '/government/verify-smes' },
      { icon: Award, label: t('sidebar.cooReview'), href: '/government/coo-review' },
      { icon: FileText, label: t('sidebar.documents'), href: '/government/documents' },
      { icon: Globe, label: t('sidebar.tradeAnalytics'), href: '/government/analytics' },
      { icon: Settings, label: t('sidebar.settings'), href: '/government/settings' },
    ],
    admin: [
      { icon: Home, label: t('sidebar.dashboard'), href: '/admin/dashboard' },
      { icon: Users, label: t('sidebar.users'), href: '/admin/users' },
      { icon: Building2, label: t('sidebar.businesses'), href: '/admin/businesses' },
      { icon: Globe, label: t('sidebar.nationalNodes'), href: '/admin/nodes' },
      { icon: Award, label: t('sidebar.cooReview'), href: '/government/coo-review' },
      { icon: Shield, label: t('sidebar.verifications'), href: '/admin/verifications' },
      { icon: Settings, label: t('sidebar.settings'), href: '/admin/settings' },
    ],
  };

  // Get links for the role, default to SME
  return links[role] || links.sme;
};

const VerificationBadge = ({ status, collapsed, t }) => {
  const statusConfig = {
    verified: { bg: 'bg-green-500/20', text: 'text-green-400', labelKey: 'sidebar.verificationStatus.verified', short: 'V' },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', labelKey: 'sidebar.verificationStatus.pending', short: 'P' },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', labelKey: 'sidebar.verificationStatus.rejected', short: 'R' },
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  
  if (collapsed) {
    return (
      <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
        {config.short}
      </span>
    );
  }
  
  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
      {t(config.labelKey)}
    </span>
  );
};

// Storage key for sidebar state
const SIDEBAR_COLLAPSED_KEY = 'atff_sidebar_collapsed';

export const DashboardSidebar = ({ className = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  
  // Initialize collapsed state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return stored === 'true';
    }
    return false;
  });
  
  // Mobile menu state
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  
  const role = user?.user_type || user?.role || 'sme';
  const sidebarLinks = getSidebarLinks(role, t);
  
  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);
  
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const getRoleLabel = (role) => {
    const labelKeys = {
      sme: 'sidebar.smeTrader',
      buyer: 'sidebar.buyer',
      logistics: 'sidebar.logisticsProvider',
      finance: 'sidebar.financialInstitution',
      government: 'sidebar.nationalNode',
      admin: 'sidebar.administrator',
    };
    return t(labelKeys[role] || 'sidebar.smeTrader');
  };
  
  const getRoleColor = (role) => {
    const colors = {
      sme: 'bg-continental/20 text-continental',
      buyer: 'bg-blue-500/20 text-blue-400',
      logistics: 'bg-orange-500/20 text-orange-400',
      finance: 'bg-purple-500/20 text-purple-400',
      government: 'bg-blue-600/20 text-blue-300',
      admin: 'bg-red-500/20 text-red-400',
    };
    return colors[role] || 'bg-continental/20 text-continental';
  };
  
  // Helper to check if link is active
  const isLinkActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* User Profile */}
      <div className={`mb-6 flex-shrink-0 ${isCollapsed ? 'px-2' : ''}`}>
        <div className={`flex items-center gap-3 mb-3 ${isCollapsed ? 'justify-center' : ''}`}>
          <div className={`${isCollapsed ? 'w-10 h-10' : 'w-12 h-12'} ${getRoleColor(role)} rounded-xl flex items-center justify-center flex-shrink-0`}>
            <User className={`${isCollapsed ? 'w-5 h-5' : 'w-6 h-6'}`} />
          </div>
          {!isCollapsed && (
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-white truncate">{user?.name || 'User'}</p>
              <p className="text-sm text-gray-400 truncate">{user?.company_name || getRoleLabel(role)}</p>
            </div>
          )}
        </div>
        <div className={isCollapsed ? 'flex justify-center' : ''}>
          <VerificationBadge status={user?.verification_status || 'pending'} collapsed={isCollapsed} t={t} />
        </div>
      </div>

      {/* Navigation Links - scrollable */}
      <nav className="space-y-1 flex-1 overflow-y-auto min-h-0">
        {sidebarLinks.map((link) => (
          <Link
            key={link.href}
            to={link.href}
            title={isCollapsed ? link.label : undefined}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isLinkActive(link.href)
                ? 'bg-continental text-white'
                : 'text-gray-400 hover:bg-white/5 hover:text-white'
            } ${isCollapsed ? 'justify-center px-2' : ''}`}
            data-testid={`sidebar-${link.href.replace(/\//g, '-').slice(1)}`}
          >
            <link.icon className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && <span>{link.label}</span>}
          </Link>
        ))}
      </nav>

      {/* Collapse Toggle & Logout */}
      <div className="mt-auto pt-6 border-t border-white/10 space-y-2">
        {/* Collapse Toggle Button */}
        <Button
          variant="ghost"
          onClick={toggleCollapse}
          className={`w-full text-gray-400 hover:text-white hover:bg-white/5 ${isCollapsed ? 'justify-center px-2' : 'justify-start'}`}
          data-testid="sidebar-collapse-btn"
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5 me-3 rtl-flip" />
              {t('sidebar.collapse')}
            </>
          )}
        </Button>
        
        {/* Logout Button */}
        <Button
          variant="ghost"
          onClick={handleLogout}
          className={`w-full text-gray-400 hover:text-white hover:bg-white/5 ${isCollapsed ? 'justify-center px-2' : 'justify-start'}`}
          data-testid="logout-sidebar-btn"
          title={isCollapsed ? t('sidebar.logout') : undefined}
        >
          <LogOut className={`w-5 h-5 ${isCollapsed ? '' : 'me-3'}`} />
          {!isCollapsed && t('sidebar.logout')}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed bottom-4 start-4 z-40 w-12 h-12 bg-charcoal text-white rounded-full shadow-lg flex items-center justify-center hover:bg-charcoal/90 transition-colors"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-6 h-6" />
      </button>
      
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      
      {/* Mobile Sidebar */}
      <aside 
        className={`lg:hidden fixed top-0 start-0 z-50 h-full w-64 bg-charcoal p-6 transform transition-transform duration-300 ease-in-out flex flex-col ${
          isMobileOpen ? 'translate-x-0' : 'ltr:-translate-x-full rtl:translate-x-full'
        }`}
      >
        <button
          onClick={() => setIsMobileOpen(false)}
          className="absolute top-4 end-4 text-gray-400 hover:text-white"
        >
          <ChevronLeft className="w-6 h-6 rtl-flip" />
        </button>
        {sidebarContent}
      </aside>
      
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:flex lg:flex-col flex-shrink-0 sticky top-20 self-start bg-charcoal h-[calc(100vh-80px)] p-6 transition-all duration-300 ease-in-out ${
          isCollapsed ? 'w-20' : 'w-64'
        } ${className}`}
        data-testid="dashboard-sidebar"
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default DashboardSidebar;
